import { JSX, useMemo } from 'react'
import { Badge, Button, Modal, Table } from 'react-bootstrap'
import { t } from '../locale'
import { isRecord } from '../helpers/valueGuards'

interface AmlChecksProps {
  show: boolean
  aml?: unknown
  onClose?: () => void
}

type AnyRecord = Record<string, unknown>

const ATTR_HEADER_KEYS = new Set(['name', 'datasets', 'type', 'id'])

const HIDDEN_KEYS = new Set(['isDeceased', 'isDeleted', 'version'])

const PRIORITY_KEYS = [
  'firstName',
  'lastName',
  'gender',
  'dateOfBirthISO',
  'datesOfBirthISO',
  'dateOfBirth',
  'middleName',
  'nationalitiesISOCodes',
]

function priorityIndex(key: string): number {
  const i = PRIORITY_KEYS.indexOf(key)
  return i === -1 ? PRIORITY_KEYS.length : i
}

const FIELD_LABEL_KEYS: Record<string, string> = {
  addressType: 'amlAddressType',
  captureDateISO: 'amlCaptureDate',
  countryISOCode: 'country',
  dateFromISO: 'amlDateFrom',
  dateISO: 'date',
  dateToISO: 'amlDateTo',
  dateOfBirth: 'dateOfBirth',
  dateOfBirthISO: 'dateOfBirth',
  datesOfBirthISO: 'dateOfBirth',
  firstName: 'firstName',
  lastName: 'lastName',
  middleName: 'middleName',
  line1: 'amlAddressLine1',
  line2: 'amlAddressLine2',
  nationalitiesISOCodes: 'amlNationalities',
  pepTier: 'amlPepTier',
  postcode: 'postcode',
  publicationDateISO: 'amlPublicationDate',
  sanctionID: 'amlSanctionId',
}

const SECTION_LABEL_KEYS: Record<string, string> = {
  addresses: 'amlAddresses',
  aliases: 'amlAliases',
  businessLinks: 'amlLinkedBusinesses',
  contactEntries: 'amlContacts',
  datesOfBirthISO: 'dateOfBirth',
  datesOfDeathISO: 'amlDatesOfDeath',
  ddEntries: 'amlDisqualification',
  evidences: 'amlEvidence',
  griEntries: 'amlGeneralRisk',
  identifiers: 'amlIdentifiers',
  individualLinks: 'amlLinkedPersons',
  insEntries: 'amlInsolvency',
  links: 'amlProviderLinks',
  pepByAssociationEntries: 'amlPepByAssociation',
  pepEntries: 'amlPepPositions',
  poiEntries: 'amlPersonsOfInterest',
  profileImages: 'amlProfileImages',
  relEntries: 'amlRegulatoryEvents',
  rreEntries: 'amlRiskEvents',
  sanEntries: 'amlSanctions',
}

function fieldLabel(key: string): string {
  const dictKey = FIELD_LABEL_KEYS[key] ?? SECTION_LABEL_KEYS[key]
  if (dictKey) return t(dictKey)
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function isIdKey(key: string): boolean {
  return /(^|[a-z])id(s)?$/i.test(key)
}

function formatMaybeDate(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value
}

function renderEntry([key, value]: [string, unknown], depth: number): JSX.Element | null {
  if (key === 'profileImages' && Array.isArray(value)) {
    const urls = value
      .map(v =>
        typeof v === 'string'
          ? v
          : isRecord(v)
            ? (v.url ?? v.uri ?? v.link ?? v.href ?? v.source)
            : null
      )
      .filter((u): u is string => typeof u === 'string' && u.trim() !== '')
    if (urls.length === 0) return null
    return (
      <div key={key} className='mb-1'>
        <b>{fieldLabel(key)}</b>
        <div className='d-flex flex-wrap gap-2 mt-1'>
          {urls.map((url, i) => (
            <a key={i} href={url} target='_blank' rel='noreferrer'>
              <img
                src={url}
                alt=''
                className='rounded border'
                style={{ maxHeight: 120, maxWidth: 120, objectFit: 'cover' }}
                onError={e => {
                  const anchor = e.currentTarget.parentElement
                  if (anchor) anchor.style.display = 'none'
                }}
              />
            </a>
          ))}
        </div>
      </div>
    )
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    const allPrimitive = value.every(v => !isRecord(v) && !Array.isArray(v))
    if (allPrimitive) {
      return (
        <div key={key}>
          <b>{fieldLabel(key)}</b>: {value.map(v => formatMaybeDate(String(v))).join(', ')}
        </div>
      )
    }
    return (
      <details key={key} className='mb-2' open={depth >= 1}>
        <summary style={{ cursor: 'pointer' }}>
          <b>{fieldLabel(key)}</b> <span className='text-muted'>({value.length})</span>
        </summary>
        <div className='ps-3 mt-1'>
          {value.map((v, i) => (
            <div key={i} className='border-start ps-2 mt-1 mb-1'>
              {isRecord(v) ? (
                renderAttrs(v, undefined, depth + 1)
              ) : (
                <span>{formatMaybeDate(String(v))}</span>
              )}
            </div>
          ))}
        </div>
      </details>
    )
  }
  if (isRecord(value)) {
    const inner = renderAttrs(value, undefined, depth + 1)
    if (inner.length === 0) return null
    return (
      <details key={key} className='mb-2' open={depth >= 1}>
        <summary style={{ cursor: 'pointer' }}>
          <b>{fieldLabel(key)}</b> <span className='text-muted'>({Object.keys(value).length})</span>
        </summary>
        <div className='ps-3 mt-1'>{inner}</div>
      </details>
    )
  }
  const text = typeof value === 'boolean' ? (value ? 'Ja' : 'Nein') : formatMaybeDate(String(value))
  return (
    <div key={key}>
      <b>{fieldLabel(key)}</b>: {text}
    </div>
  )
}

function renderAttrs(attrs: AnyRecord, skipKeys?: Set<string>, depth = 0): JSX.Element[] {
  const entries = Object.entries(attrs).filter(([key, value]) => {
    if (skipKeys?.has(key)) return false
    if (key === 'datasets') return false
    if (HIDDEN_KEYS.has(key)) return false
    if (isIdKey(key)) return false
    if (value === null || value === undefined || value === '') return false
    if (typeof value === 'string' && value.toUpperCase() === 'UNKNOWN') return false
    return true
  })

  const imagesEntry = entries.find(([key]) => key === 'profileImages')
  const images = imagesEntry ? renderEntry(imagesEntry, depth) : null

  const priority = entries
    .filter(([key]) => key !== 'profileImages' && priorityIndex(key) < PRIORITY_KEYS.length)
    .sort(([a], [b]) => priorityIndex(a) - priorityIndex(b))
    .map(e => renderEntry(e, depth))
    .filter((el): el is JSX.Element => el !== null)

  const rest = entries
    .filter(([key]) => key !== 'profileImages' && priorityIndex(key) >= PRIORITY_KEYS.length)
    .map(e => renderEntry(e, depth))
    .filter((el): el is JSX.Element => el !== null)

  if (priority.length === 0 && !images) return rest

  const top = (
    <div key='__top' className='d-flex flex-wrap justify-content-between gap-3'>
      <div className='flex-grow-1 mt-2'>{priority}</div>
      {images && <div className='flex-shrink-0'>{images}</div>}
    </div>
  )

  if (rest.length === 0) return [top]

  return [
    top,
    <div key='__rest' className='mt-3 pt-3 border-top'>
      {rest}
    </div>,
  ]
}

function getAttrs(item: AnyRecord): AnyRecord {
  if (isRecord(item.data) && isRecord((item.data as AnyRecord).attributes)) {
    return (item.data as AnyRecord).attributes as AnyRecord
  }
  if (isRecord(item.attributes)) return item.attributes as AnyRecord
  return {}
}

function getDatasets(item: AnyRecord): string[] {
  const attrs = getAttrs(item)
  const raw =
    attrs.datasets ??
    (isRecord(item.data) ? (item.data as AnyRecord).datasets : undefined) ??
    item.datasets
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string')
  if (typeof raw === 'string') return [raw]
  return []
}

function getAmlName(item: AnyRecord): string | null {
  const attrs = getAttrs(item)
  const full = [attrs.firstName, attrs.middleName, attrs.lastName]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .join(' ')
  return (
    full ||
    (typeof attrs.name === 'string' ? attrs.name : null) ||
    (typeof item.name === 'string' ? item.name : null)
  )
}

function getAmlType(item: AnyRecord): string {
  const data = isRecord(item.data) ? (item.data as AnyRecord) : item
  const type = data.type ?? item.type
  if (type === 'persons') return 'Person'
  if (type === 'businesses') return 'Company'
  return typeof type === 'string' ? type : 'AML result'
}

// Stable id of a single matched entity within an AML check, used to delete /
// restore that person without touching the rest of the check.
export function getAmlHitId(item: AnyRecord): string | null {
  const data = isRecord(item.data) ? (item.data as AnyRecord) : null
  const id = (data ? data.id : undefined) ?? item.id
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null
}

export function normalizeAml(value: unknown): AnyRecord[] {
  const result: AnyRecord[] = []
  const collect = (v: unknown) => {
    if (Array.isArray(v)) {
      v.forEach(collect)
      return
    }
    if (isRecord(v)) result.push(v)
  }
  collect(value)
  return result
}

export function AmlHit({
  item,
  onDelete,
  onRestore,
}: {
  item: AnyRecord
  onDelete?: () => void
  onRestore?: () => void
}) {
  const name = getAmlName(item)
  const type = getAmlType(item)
  const datasets = getDatasets(item)
  const attrs = getAttrs(item)

  return (
    <div className='mb-3 pb-2 border-bottom'>
      <div className='mb-1 d-flex align-items-center flex-wrap gap-2'>
        {name && <strong>{name}</strong>}
        <Badge bg='primary'>{type}</Badge>
        {onRestore && (
          <Button variant='outline-success' size='sm' className='ms-auto' onClick={onRestore}>
            <i className='bi bi-arrow-counterclockwise me-1' />
            {t('restore')}
          </Button>
        )}
        {onDelete && (
          <Button variant='outline-danger' size='sm' className='ms-auto' onClick={onDelete}>
            <i className='bi bi-trash me-1' />
            {t('delete')}
          </Button>
        )}
      </div>

      {datasets.length > 0 && (
        <div className='mb-2'>
          <b>{t('datasets')}</b>: {datasets.join(', ')}
        </div>
      )}

      {renderAttrs(attrs, ATTR_HEADER_KEYS)}
    </div>
  )
}

function AmlChecks({ show, aml, onClose }: AmlChecksProps) {
  const items = useMemo(() => normalizeAml(aml), [aml])

  return (
    <Modal show={show} centered scrollable size='lg' onHide={onClose}>
      <Modal.Header className='bg-secondary text-white' closeButton>
        <Modal.Title>
          <i className='bi bi-search me-2' />
          AML Search
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {items.length === 0 ? (
          <p className='text-muted mb-0'>{t('noResults')}</p>
        ) : (
          <Table borderless size='lg' className='mb-2'>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className='mb-4'>
                  <td>
                    <AmlHit item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AmlChecks
